/**
 * Default transit network preferences (HTTM-backed, user + customer).
 *
 * A user opens the map to a default network on sign-in. Resolution is
 * **user override → customer default → none**, computed server-side by HTTM
 * (where user/customer auth lives). Company admins set the customer-wide default
 * that cascades to everyone in the org.
 *
 * Uses `atlasAuthApi` (base = HTTM `REACT_APP_API_BASE_URL`, JWT attached). The
 * read is rollout-safe: if the endpoint isn't deployed yet (404/405) or is
 * unreachable, it resolves to "no default" so the app still works.
 */

import axios from 'axios';

import { atlasAuthApi } from '../api/atlasAuthApi';

export type NetworkPreferenceSource = 'user' | 'customer' | 'none';

export interface NetworkPreference {
  /** The resolved default to open to (user override, else customer default, else null). */
  default_network_feed_id: string | null;
  source: NetworkPreferenceSource;
  /** The user's own override (null when they inherit the org default). */
  user_default_network_feed_id: string | null;
  /** The org-wide default, surfaced so the UI can show what's inherited. */
  customer_default_network_feed_id: string | null;
}

const NONE: NetworkPreference = {
  default_network_feed_id: null,
  source: 'none',
  user_default_network_feed_id: null,
  customer_default_network_feed_id: null,
};

const USER_PREFS = '/data_generator/user/preferences';
const COMPANY_DEFAULT = '/company/default-network';

/** Endpoint not deployed (404/405/501) or unreachable (no response / 401 = signed out). */
function isUnavailable(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    return status === undefined || status === 404 || status === 405 || status === 501 || status === 401;
  }
  return true;
}

/** Resolved default-network preference for the current user. Never throws — falls back to "none". */
export async function getNetworkPreference(): Promise<NetworkPreference> {
  try {
    const res = await atlasAuthApi.get<NetworkPreference>(USER_PREFS, { timeout: 12000 });
    return res.data ?? NONE;
  } catch (error) {
    if (isUnavailable(error)) return NONE;
    throw error;
  }
}

/** Set (or clear, with null) the current user's personal default network. */
export async function setUserDefaultNetwork(feedId: string | null): Promise<NetworkPreference> {
  const res = await atlasAuthApi.put<NetworkPreference>(
    USER_PREFS,
    { default_network_feed_id: feedId },
    { timeout: 12000 },
  );
  return res.data;
}

/** Company-wide default network (company admins only). Returns null when unset. */
export async function getCompanyDefaultNetwork(): Promise<string | null> {
  const res = await atlasAuthApi.get<{ default_network_feed_id: string | null }>(COMPANY_DEFAULT, {
    timeout: 12000,
  });
  return res.data?.default_network_feed_id ?? null;
}

/** Set (or clear, with null) the company-wide default network (company admins only). */
export async function setCompanyDefaultNetwork(feedId: string | null): Promise<string | null> {
  const res = await atlasAuthApi.put<{ default_network_feed_id: string | null }>(
    COMPANY_DEFAULT,
    { default_network_feed_id: feedId },
    { timeout: 12000 },
  );
  return res.data?.default_network_feed_id ?? null;
}
